import { useCallback, useState } from 'react'

import type { PlateRenderNodeProps, TRenderElementProps } from '@udecode/plate'
import { useSelected } from 'slate-react'

import { getPublishUrl } from '@buffer-mono/app-shell/src/common/utils/urls'
import { Service } from '@buffer-mono/app-shell/src/exports/Orchestrator/channelConnections/types'
import { Button, CoachMark, SimpleSpinner } from '@buffer-mono/popcorn'

import AppActionCreators from '~publish/legacy/composer/composer/action-creators/AppActionCreators'
import AppStore from '~publish/legacy/composer/composer/stores/AppStore'
import { useSaveCallbacks } from '~publish/legacy/composer/composer/utils/useSaveCallbacks'
import type {
  BufferEditor,
  BufferValue,
} from '~publish/legacy/editor/BufferEditor/types.plate'
import {
  EditorTooltip,
  TooltipButton,
  TooltipText,
} from '~publish/legacy/editor/components/EditorTooltip'

import type { LinkedInAnnotationInputElement } from '../nodes/LinkedInAnnotationInputElement'

import styles from './LinkedInMentionUI.module.css'

type LinkedInMentionUIProps = PlateRenderNodeProps<BufferValue, BufferEditor> &
  TRenderElementProps<BufferValue, LinkedInAnnotationInputElement>

export const LinkedInMentionUI = ({
  attributes,
  children,
  element,
}: LinkedInMentionUIProps): JSX.Element | null => {
  const selected = useSelected()
  const [isSavingAsDraft, setIsSavingAsDraft] = useState(false)
  const { saveAsDraft } = useSaveCallbacks()

  const onOpenClick = useCallback(() => {
    window.open(element.data?.link, '_blank')
  }, [element.data?.link])

  if (!element.data) {
    if (!selected) {
      return children
    }

    return (
      <>
        {element.isPermissionError ? (
          <CoachMark
            onDismiss={(): void => {
              element.isPermissionError = false
            }}
            open
          >
            <CoachMark.Overlay style={{ display: 'inline' }}>
              <span className={styles.wrapper} contentEditable={false}>
                {children}
              </span>
            </CoachMark.Overlay>

            <CoachMark.Content>
              <CoachMark.Title>
                Let&apos;s setup LinkedIn mentions!
              </CoachMark.Title>
              <CoachMark.Description>
                To get everything ready for mentioning, a quick channel refresh
                is needed.
              </CoachMark.Description>
              <CoachMark.Footer>
                <CoachMark.Dismiss>
                  <Button variant="tertiary">Not Now</Button>
                </CoachMark.Dismiss>

                <Button
                  variant="primary"
                  disabled={isSavingAsDraft}
                  onClick={(): void => {
                    const orgData = AppStore.getOrganizationsData()
                    const channelId = element.channelId

                    if (!channelId) {
                      return
                    }

                    const isNewPostMode = AppStore.getIsNewPostMode()

                    // Try to use appshell actions if available
                    if (
                      window.appshell?.actions &&
                      window.appshell.actions.connectChannel
                    ) {
                      // Check if we can save the draft first
                      const { isSavingPossible, isSavingDraftPossible } =
                        AppStore.getAppState()

                      if (!isSavingPossible && !isSavingDraftPossible) {
                        // If we can't save (e.g., empty post), redirect back to the queue page
                        const destinationUrl = getPublishUrl(
                          `channels/${channelId}`,
                        )
                        setIsSavingAsDraft(true)

                        window.appshell.actions.connectChannel({
                          selectedService: Service.linkedin,
                          isRefreshingConnection: true,
                          skipCelebrationModal: true,
                          cta: 'publish-composer-linkedinMentions-refresh-1',
                          selectedRefreshChannelId: channelId,
                          role: orgData.selected.isAdmin ? 'admin' : 'member',
                          destinationUrl,
                        })
                        return
                      }

                      // If we can save, save first then connect
                      setIsSavingAsDraft(true)

                      const savePromise = isNewPostMode
                        ? saveAsDraft()
                        : AppActionCreators.saveDrafts('SAVE', {
                            shouldSkipEmptyTextAlert: true,
                            hasCampaignsToTagsFeature: true,
                          })

                      savePromise
                        .then(({ successfulResponses }) => {
                          const updateId =
                            successfulResponses[0]?.updates?.find(
                              (update) => update.profile_id === channelId,
                            )?.id

                          const destinationUrl = getPublishUrl(
                            `channels/${channelId}${
                              updateId
                                ? `/posts/${updateId}/edit`
                                : '?tab=drafts'
                            }`,
                          )

                          return window.appshell?.actions?.connectChannel({
                            selectedService: Service.linkedin,
                            isRefreshingConnection: true,
                            skipCelebrationModal: true,
                            cta: 'publish-composer-linkedinMentions-refresh-1',
                            selectedRefreshChannelId: channelId,
                            role: orgData.selected.isAdmin ? 'admin' : 'member',
                            destinationUrl,
                          })
                        })
                        .catch(() => {
                          setIsSavingAsDraft(false)
                        })
                    } else {
                      // When appshell is not available, redirect to settings
                      setIsSavingAsDraft(true)

                      const savePromise = isNewPostMode
                        ? saveAsDraft()
                        : AppActionCreators.saveDrafts('SAVE', {
                            shouldSkipEmptyTextAlert: true,
                            hasCampaignsToTagsFeature: true,
                          })

                      savePromise
                        .then((): void => {
                          // Redirect to channels settings page where user can refresh manually
                          window.location.href =
                            getPublishUrl('settings/channels')
                        })
                        .catch(() => {
                          setIsSavingAsDraft(false)
                        })
                    }
                  }}
                >
                  {isSavingAsDraft ? (
                    <>
                      <SimpleSpinner /> Saving as Draft
                    </>
                  ) : (
                    'Refresh'
                  )}
                </Button>
              </CoachMark.Footer>
            </CoachMark.Content>
          </CoachMark>
        ) : (
          <span className={styles.wrapper}>
            {children}
            {element.children[0].text.length === 1 && selected ? (
              <span className={styles.helpText} contentEditable={false}>
                Mention a <strong>connection</strong> or paste a{' '}
                <strong>LinkedIn Page URL</strong>
              </span>
            ) : null}
            {element.isLoading && <SimpleSpinner className={styles.spinner} />}
          </span>
        )}
      </>
    )
  }

  const { link } = element.data

  // this has to be able to display both autocomplete and pasted mentions as urls
  return (
    <span className={styles.mention} {...attributes}>
      <EditorTooltip trigger={<span>{children}</span>}>
        <TooltipText title={link}>{link}</TooltipText>
        <TooltipButton onClick={onOpenClick}>Open</TooltipButton>
      </EditorTooltip>
    </span>
  )
}
